import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import pdfIcon from "../../../../assets/images/pdfIcon.svg";
import excelIcon from "../../../../assets/images/Cexcel.svg";
import pngIcon from "../../../../assets/images/Cgallery.svg";
import csvIcon from "../../../../assets/images/CSVIcon.svg";
import VerticalBarChart from "./VerticalBarChart";
import HorizontalBarChart from "./HorizontalBarChart";
import MultiLineChart from "./MultiLineChart";
import CustomComposedChart from "./ComposedChart";
import {
  PIE_OTHERS_COLOR,
  PIE_COLORS,
  formatLabelText,
  formatMultiLineGraph,
  formatComposedChart,
  formatPieChart,
  formatHeatMapData,
  groupTopPieItemsWithOthers,
  getDomain,
} from "./chartFormatters";

// ── Export-only constants ──
export const sanitizeFilename = (name) =>
  (name || "export").replace(/[^a-z0-9-_]+/gi, "_").slice(0, 80) || "export";

const COLUMNS_PER_PAGE = 14;
const POINTS_PER_PAGE = 14;
const SERIES_CHUNK_OVERLAP = 1;
const EXPORT_CHART_WIDTH = 1200;
const EXPORT_CHART_HEIGHT = 500;
const SVG_PNG_MIN_OUTPUT_WIDTH = 1800;
const MAX_CANVAS_DIM = 8192;
const SVG_NS = "http://www.w3.org/2000/svg";
const CHARTS_PER_PAGE = 2;

const EXPORT_BAR_COLOR = "#B6F7FF";
const EXPORT_COMPOSED_BAR_COLOR = "#A3D9A5";
const EXPORT_COMPOSED_LINE_COLOR = "#FF7043";

const PAGINATABLE_GRAPH_TYPES = new Set([
  "barGraph",
  "multiLineGraphs",
  "composedBarLineChart",
]);
const isPaginatableChartType = (graphType) =>
  PAGINATABLE_GRAPH_TYPES.has(graphType);

// ── Pure helpers ──
const chunkArray = (arr, size) => {
  if (!arr?.length || size <= 0) return [];
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const chunkSeriesWithOverlap = (arr, size, overlap = SERIES_CHUNK_OVERLAP) => {
  if (!arr?.length || size <= 0) return [];
  if (arr.length <= size) return [arr.slice()];
  const step = Math.max(1, size - overlap);
  const out = [];
  for (let i = 0; i < arr.length; i += step) {
    out.push(arr.slice(i, i + size));
    if (i + size >= arr.length) break;
  }
  return out;
};

const buildTableExportData = (headerConfig, rows) => {
  if (!headerConfig?.length) return { headers: [], dataRows: [] };

  const allCells = headerConfig.flatMap((row) => row?.cells || []);
  const leafCells = allCells.filter((c) => !c?.colSpan || c.colSpan === 1);
  const headers = leafCells.map((c) => c?.label ?? "");

  const dataRows = (rows || []).map((row) => {
    if (Array.isArray(row?.cells)) {
      return row.cells.map((cell) => cell?.value ?? "");
    }
    return leafCells.map((c) => {
      const key = c?.accessor || c?.key || c?.label;
      return row?.[key] ?? "";
    });
  });

  return { headers, dataRows };
};

const pickEvenlyDistributed = (data, count) => {
  const n = data.length;
  if (n <= count) return data.slice();
  const indices = new Set();
  for (let i = 0; i < count; i += 1) {
    indices.add(Math.round((i * (n - 1)) / (count - 1)));
  }
  return Array.from(indices)
    .sort((a, b) => a - b)
    .map((idx) => data[idx]);
};

const buildGraphExportData = (graph) => {
  const data = graph?.data || [];
  if (!data.length) return { headers: [], dataRows: [] };

  const sampled = pickEvenlyDistributed(data, 4);
  const headers = Object.keys(data[0]);
  const dataRows = sampled.map((d) => headers.map((h) => d[h] ?? ""));

  return { headers, dataRows };
};

const escapeHtml = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c],
  );

const getDensityStyles = (colCount) => {
  if (colCount >= 11) return { fontSize: 9, padding: "4px 5px", h2: 14 };
  if (colCount >= 9) return { fontSize: 10, padding: "5px 7px", h2: 15 };
  if (colCount >= 7) return { fontSize: 11, padding: "6px 9px", h2: 16 };
  return { fontSize: 12, padding: "8px 12px", h2: 18 };
};

const getSvgDimensions = (svgEl) => {
  const rect = svgEl.getBoundingClientRect();
  const widthAttr = Number(svgEl.getAttribute("width")) || 0;
  const heightAttr = Number(svgEl.getAttribute("height")) || 0;
  const width = rect.width || svgEl.clientWidth || widthAttr || 800;
  const height = rect.height || svgEl.clientHeight || heightAttr || 600;
  return { width, height };
};

const collectPageStyleRules = () => {
  const out = [];
  for (const sheet of Array.from(document.styleSheets)) {
    let rules;
    try {
      rules = sheet.cssRules;
    } catch {
      continue;
    }
    if (!rules) continue;
    for (const rule of Array.from(rules)) {
      out.push(rule.cssText);
    }
  }
  return out.join("\n");
};

const A4_PRINTABLE = {
  marginMM: 10,
  portrait: { widthMM: 190, heightMM: 277 },
  landscape: { widthMM: 277, heightMM: 190 },
};

const chooseA4Orientation = (width, height) => {
  if (!width || !height) return "portrait";
  return width / height > 1.15 ? "landscape" : "portrait";
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const HEATMAP_BANDS = [
  { min: 5, bg: "#1F36C7B2", color: "#ffffff" },
  { min: 3, bg: "#8F9AE3", color: "#000000" },
  { min: 0, bg: "#8F9AE366", color: "#000000" },
];

const getHeatmapCellStyle = (value) => {
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return null;
  return HEATMAP_BANDS.find((band) => num >= band.min) || null;
};

// ── Canvas/blob helpers ──
const svgToPngBlob = (
  svgEl,
  { minOutputWidth = SVG_PNG_MIN_OUTPUT_WIDTH } = {},
) =>
  new Promise((resolve, reject) => {
    try {
      const clone = svgEl.cloneNode(true);
      clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      const { width, height } = getSvgDimensions(svgEl);
      clone.setAttribute("width", width);
      clone.setAttribute("height", height);

      const svgStr = new XMLSerializer().serializeToString(clone);
      const svgBlob = new Blob([svgStr], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = () => {
        const baseScale = 2;
        const dpiScale = minOutputWidth / width;
        const desiredScale = Math.max(baseScale, dpiScale);
        const maxScale = Math.min(
          MAX_CANVAS_DIM / width,
          MAX_CANVAS_DIM / height,
        );
        const scale = Math.max(1, Math.min(desiredScale, maxScale));
        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(width * scale);
        canvas.height = Math.floor(height * scale);
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(url);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
          "image/png",
        );
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };
      img.src = url;
    } catch (e) {
      reject(e);
    }
  });

const containerToPngBlob = (node, { minOutputWidth = 1800 } = {}) =>
  new Promise((resolve, reject) => {
    if (!node) {
      reject(new Error("containerToPngBlob: no node"));
      return;
    }
    const rect = node.getBoundingClientRect();
    const width = Math.max(Math.ceil(rect.width || node.scrollWidth || 0), 1);
    const height = Math.max(
      Math.ceil(rect.height || node.scrollHeight || 0),
      1,
    );
    const cssText = collectPageStyleRules();

    const svgStr = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
      <foreignObject width="100%" height="100%">
        <div xmlns="http://www.w3.org/1999/xhtml" style="width:${width}px;height:${height}px;background:#ffffff;">
          <style>${cssText}</style>
          ${node.outerHTML}
        </div>
      </foreignObject>
    </svg>`;

    const blob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const baseScale = 2;
      const dpiScale = minOutputWidth / width;
      const desiredScale = Math.max(baseScale, dpiScale);
      const maxScale = Math.min(
        MAX_CANVAS_DIM / width,
        MAX_CANVAS_DIM / height,
      );
      const scale = Math.max(1, Math.min(desiredScale, maxScale));
      const canvas = document.createElement("canvas");
      canvas.width = Math.floor(width * scale);
      canvas.height = Math.floor(height * scale);
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
        "image/png",
      );
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });

const htmlToPngBlob = (htmlContent, targetWidth = 1600) =>
  new Promise((resolve, reject) => {
    const measure = document.createElement("div");
    measure.style.cssText = `position:fixed;left:-99999px;top:0;width:${targetWidth}px;`;
    measure.innerHTML = htmlContent;
    document.body.appendChild(measure);
    const width = Math.max(targetWidth, measure.scrollWidth);
    const height = measure.scrollHeight + 24;
    document.body.removeChild(measure);

    const svgStr = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
      <foreignObject width="100%" height="100%">
        <div xmlns="http://www.w3.org/1999/xhtml" style="width:${width}px;background:#ffffff;">${htmlContent}</div>
      </foreignObject>
    </svg>`;

    const blob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const maxScale = Math.min(
        MAX_CANVAS_DIM / width,
        MAX_CANVAS_DIM / height,
      );
      const scale = Math.max(1, Math.min(2, maxScale));
      const canvas = document.createElement("canvas");
      canvas.width = Math.floor(width * scale);
      canvas.height = Math.floor(height * scale);
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
        "image/png",
      );
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });

// ── HTML builders for print ──
const tableDataToHtml = (headers, dataRows, title) => {
  if (!headers?.length) return "";

  const headerChunks = chunkArray(headers, COLUMNS_PER_PAGE);
  const totalPages = headerChunks.length;
  const firstChunkSize = headerChunks[0].length;
  const orientation = firstChunkSize > 6 ? "landscape" : "portrait";
  const { fontSize, padding, h2 } = getDensityStyles(firstChunkSize);

  const renderSection = (chunkHeaders, pageIndex) => {
    const startIdx = pageIndex * COLUMNS_PER_PAGE;
    const endIdx = startIdx + chunkHeaders.length - 1;
    const colWidth = `${(100 / chunkHeaders.length).toFixed(4)}%`;
    const headerRow = chunkHeaders
      .map((h) => `<th>${escapeHtml(h)}</th>`)
      .join("");
    const bodyRows = (dataRows || [])
      .map((row) => {
        const slice = (row || []).slice(
          startIdx,
          startIdx + chunkHeaders.length,
        );
        return `<tr>${slice
          .map((c) => `<td>${escapeHtml(c)}</td>`)
          .join("")}</tr>`;
      })
      .join("");

    return `
      <section class="pdf-section${pageIndex > 0 ? " page-break" : ""}">
        <div class="title-row">
          ${
            totalPages > 1
              ? `<span class="page-info">Page ${pageIndex + 1} of ${totalPages} · Columns ${startIdx + 1}–${endIdx + 1} of ${headers.length}</span>`
              : ""
          }
        </div>
        <div class="table-wrap">
          <table style="font-size:${fontSize}px;">
            <colgroup>${chunkHeaders.map(() => `<col style="width:${colWidth};" />`).join("")}</colgroup>
            <thead><tr>${headerRow}</tr></thead>
            <tbody>${bodyRows}</tbody>
          </table>
        </div>
      </section>
    `;
  };

  return `
    <style>
      @page { size: A4 ${orientation}; margin: 10mm; }
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; }
      body { font-family: Arial, Helvetica, sans-serif; padding: 16px; color: #1A2025; }
      .pdf-section { width: 100%; }
      .pdf-section + .pdf-section { margin-top: 24px; }
      .page-break { page-break-before: always; break-before: page; }
      .title-row {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 12px;
        margin: 0 0 12px;
      }
      h2 { margin: 0; font-size: ${h2}px; }
      .page-info { font-size: ${Math.max(10, fontSize)}px; color: #4D5F6E; white-space: nowrap; }
      .table-wrap { width: 100%; overflow-x: auto; }
      table { border-collapse: collapse; width: 100%; table-layout: fixed; }
      th, td {
        border: 1px solid #D9E1E7;
        padding: ${padding};
        text-align: left;
        vertical-align: top;
        word-break: break-word;
        overflow-wrap: anywhere;
        white-space: normal;
      }
      th { background: #F1F5F9; font-weight: 600; }
      tr:nth-child(even) td { background: #FAFBFC; }
      @media print {
        body { padding: 0; }
        table { page-break-inside: auto; }
        tr { page-break-inside: avoid; page-break-after: auto; }
        thead { display: table-header-group; }
        tfoot { display: table-footer-group; }
      }
    </style>
    ${headerChunks.map((chunk, i) => renderSection(chunk, i)).join("")}
  `;
};

const heatmapToHtml = (rowHeaderTitle, columns, rows, title, lastRefreshed) => {
  if (!columns?.length || !rows?.length) return "";

  const columnChunks = chunkArray(columns, COLUMNS_PER_PAGE);
  const totalPages = columnChunks.length;
  const firstChunkSize = columnChunks[0].length + 1;
  const orientation = firstChunkSize > 6 ? "landscape" : "portrait";
  const { fontSize, padding, h2 } = getDensityStyles(firstChunkSize);

  const renderSection = (chunkColumns, pageIndex) => {
    const startIdx = pageIndex * COLUMNS_PER_PAGE;
    const endIdx = startIdx + chunkColumns.length - 1;
    const dataColPct = `${(75 / chunkColumns.length).toFixed(4)}%`;

    const headerCells = chunkColumns
      .map((col) => `<th class="col-th">${escapeHtml(col)}</th>`)
      .join("");

    const bodyRows = rows
      .map((row) => {
        const cells = chunkColumns
          .map((col) => {
            const raw = row?.values?.[col];
            const isEmpty = raw === undefined || raw === null || raw === "-";
            const display =
              typeof raw === "number" ? raw.toLocaleString() : (raw ?? "-");
            if (isEmpty) {
              return `<td class="data-td"><span class="cell">${escapeHtml(display)}</span></td>`;
            }
            const style = getHeatmapCellStyle(raw);
            if (!style) {
              return `<td class="data-td"><span class="cell">${escapeHtml(display)}</span></td>`;
            }
            return `<td class="data-td"><span class="cell" style="background:${style.bg};color:${style.color};">${escapeHtml(display)}</span></td>`;
          })
          .join("");
        return `<tr><th class="row-th">${escapeHtml(row?.row ?? "")}</th>${cells}</tr>`;
      })
      .join("");

    const meta =
      totalPages > 1
        ? `<span class="page-info">Page ${pageIndex + 1} of ${totalPages} · Columns ${startIdx + 1}–${endIdx + 1} of ${columns.length}</span>`
        : lastRefreshed
          ? `<span class="last-refreshed">${escapeHtml(lastRefreshed)}</span>`
          : "";

    return `
      <section class="pdf-section${pageIndex > 0 ? " page-break" : ""}">
        <div class="title-row">
          ${pageIndex >= 0 ? `<h2>${escapeHtml(title)}</h2>` : ""}
          ${meta}
        </div>
        <div class="heatmap-wrap">
          <table class="heatmap" style="font-size:${fontSize}px;">
            <colgroup>
              <col class="row-col" />
              ${chunkColumns.map(() => `<col class="data-col" style="width:${dataColPct};" />`).join("")}
            </colgroup>
            <thead>
              <tr><th class="row-corner">${escapeHtml(rowHeaderTitle)}</th>${headerCells}</tr>
            </thead>
            <tbody>${bodyRows}</tbody>
          </table>
        </div>
      </section>
    `;
  };

  return `
    <style>
      @page { size: A4 ${orientation}; margin: 10mm; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      html, body { margin: 0; padding: 0; }
      body { font-family: Arial, Helvetica, sans-serif; padding: 16px; color: #1A2025; }
      .pdf-section { width: 100%; }
      .pdf-section + .pdf-section { margin-top: 24px; }
      .page-break { page-break-before: always; break-before: page; }
      .title-row { display: flex; justify-content: space-between; align-items: baseline; margin: 0 0 12px; gap: 12px; }
      h2 { margin: 0; font-size: ${h2}px; }
      .last-refreshed, .page-info { font-size: ${Math.max(10, fontSize - 1)}px; color: #4D5F6E; white-space: nowrap; }
      .heatmap-wrap { width: 100%; overflow-x: auto; }
      table.heatmap {
        border-collapse: separate;
        border-spacing: 0;
        width: 100%;
        table-layout: fixed;
        border: 1px solid #E0E0E0;
        border-radius: 8px;
        overflow: hidden;
      }
      table.heatmap col.row-col { width: 25%; }
      .heatmap th, .heatmap td {
        border-right: 1px solid #E0E0E0;
        border-bottom: 1px solid #E0E0E0;
        padding: ${padding};
        vertical-align: middle;
        word-break: break-word;
        overflow-wrap: anywhere;
      }
      .heatmap th:last-child, .heatmap td:last-child { border-right: 0; }
      .heatmap tr:last-child th, .heatmap tr:last-child td { border-bottom: 0; }
      .heatmap thead th {
        background: #354ACD;
        color: #fff;
        font-weight: 600;
        text-align: center;
        line-height: 1.3;
      }
      .heatmap thead th.row-corner { text-align: left; }
      .heatmap tbody th.row-th {
        background: #ffffff;
        font-weight: 500;
        text-align: left;
        color: #1A2025;
      }
      .heatmap td.data-td { text-align: center; padding: 4px; background: #ffffff; }
      .cell {
        display: block;
        padding: 6px 8px;
        border-radius: 4px;
        font-weight: 600;
        line-height: 1.3;
      }
      @media print {
        body { padding: 0; }
        table.heatmap { page-break-inside: auto; }
        tr { page-break-inside: avoid; page-break-after: auto; }
        thead { display: table-header-group; }
      }
    </style>
    ${columnChunks.map((chunk, i) => renderSection(chunk, i)).join("")}
  `;
};

const buildSvgPdfHtml = ({ dataUrl, width, height, title }) => {
  const orientation = chooseA4Orientation(width, height);
  const { widthMM, heightMM } = A4_PRINTABLE[orientation];
  const titleReserveMM = 18;
  const maxImgHeightMM = Math.max(40, heightMM - titleReserveMM);

  return `
    <style>
      @page { size: A4 ${orientation}; margin: ${A4_PRINTABLE.marginMM}mm; }
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; }
      body { font-family: Arial, Helvetica, sans-serif; color: #1A2025; }
      .pdf-page {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
      }
      .pdf-title {
        margin: 0;
        font-size: 16px;
        text-align: center;
        word-break: break-word;
      }
      .pdf-img-wrap {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .pdf-img {
        display: block;
        max-width: ${widthMM}mm;
        max-height: ${maxImgHeightMM}mm;
        width: auto;
        height: auto;
        object-fit: contain;
      }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    </style>
    <div class="pdf-page">
      <h2 class="pdf-title">${escapeHtml(title)}</h2>
      <div class="pdf-img-wrap">
        <img class="pdf-img" src="${dataUrl}" alt="${escapeHtml(title)}" />
      </div>
    </div>
  `;
};

const buildMultiPageGraphPdfHtml = ({
  dataUrls,
  title,
  width,
  height,
  totalPoints,
  pointsPerPage,
  chartsPerPage = CHARTS_PER_PAGE,
}) => {
  const orientation = chooseA4Orientation(width, height);
  const { widthMM, heightMM } = A4_PRINTABLE[orientation];
  const titleReserveMM = 14;
  const metaReserveMM = 6 * chartsPerPage;
  const gapMM = 6 * Math.max(0, chartsPerPage - 1);
  const usableHeightMM = Math.max(
    40,
    heightMM - titleReserveMM - metaReserveMM - gapMM,
  );
  const perChartHeightMM = Math.floor(usableHeightMM / chartsPerPage);

  const totalChunks = dataUrls.length;
  const totalPages = Math.ceil(totalChunks / chartsPerPage);

  const sections = [];
  for (let pageIdx = 0; pageIdx < totalPages; pageIdx++) {
    const start = pageIdx * chartsPerPage;
    const pageDataUrls = dataUrls.slice(start, start + chartsPerPage);
    const blocks = pageDataUrls
      .map((dataUrl, j) => {
        const chunkIdx = start + j;
        const ptStart = chunkIdx * pointsPerPage + 1;
        const ptEnd = Math.min((chunkIdx + 1) * pointsPerPage, totalPoints);
        const meta =
          totalChunks > 1
            ? `Chunk ${chunkIdx + 1} of ${totalChunks} · Points ${ptStart}–${ptEnd} of ${totalPoints}`
            : "";
        return `
          <div class="chart-block">
            ${meta ? `<div class="chart-meta">${escapeHtml(meta)}</div>` : ""}
            <div class="img-wrap">
              <img src="${dataUrl}" alt="${escapeHtml(title)}" />
            </div>
          </div>
        `;
      })
      .join("");

    sections.push(`
      <section class="pdf-page${pageIdx > 0 ? " page-break" : ""}">
        ${blocks}
      </section>
    `);
  }

  return `
    <style>
      @page { size: A4 ${orientation}; margin: 10mm; }
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; }
      body { font-family: Arial, Helvetica, sans-serif; color: #1A2025; }
      .pdf-page {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 6mm;
      }
      .page-break { page-break-before: always; break-before: page; }
      h2 {
        margin: 0 0 4mm;
        font-size: 14px;
        word-break: break-word;
        font-weight: 600;
      }
      .page-of { font-size: 11px; color: #4D5F6E; font-weight: 400; }
      .chart-block {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        page-break-inside: avoid;
        break-inside: avoid;
      }
      .chart-meta {
        font-size: 10px;
        color: #4D5F6E;
        margin: 0 0 2mm;
      }
      .img-wrap {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .img-wrap img {
        display: block;
        max-width: ${widthMM}mm;
        max-height: ${perChartHeightMM}mm;
        width: auto;
        height: auto;
        object-fit: contain;
      }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    </style>
    ${sections.join("")}
  `;
};

const openPrintWindow = (htmlContent, title) => {
  const win = window.open("", "_blank");
  if (!win) return;
  win.document.write(`
    <html>
      <head><title>${escapeHtml(title || "Export")}</title></head>
      <body>${htmlContent}</body>
    </html>
  `);
  win.document.close();
  win.focus();
  setTimeout(() => {
    win.print();
  }, 300);
};

// ── Legend injection (mutates SVG; cleanup function reverts) ──
const NOOP = () => {};

const buildExportLegendItems = (graph, graphType) => {
  if (!graph) return [];

  if (graphType === "multiLineGraphs") {
    const formatted = formatMultiLineGraph(graph);
    return formatted.legendItems || [];
  }

  if (graphType === "composedBarLineChart") {
    const items = [];
    if (graph?.yKey) {
      items.push({
        label: formatLabelText(String(graph.yKey)),
        color: EXPORT_COMPOSED_BAR_COLOR,
      });
    }
    if (graph?.line) {
      items.push({
        label: formatLabelText(String(graph.line)),
        color: EXPORT_COMPOSED_LINE_COLOR,
        dashed: true,
      });
    }
    return items;
  }

  if (graphType === "barGraph") {
    if (!graph?.yKey) return [];
    return [
      {
        label: formatLabelText(String(graph.yKey)),
        color: EXPORT_BAR_COLOR,
      },
    ];
  }

  if (graphType === "pieChart") {
    const formatted = formatPieChart(graph);
    const grouped = groupTopPieItemsWithOthers(formatted);
    return grouped.map((item, i) => ({
      label: String(item?.name ?? ""),
      color: item?.isOthers
        ? PIE_OTHERS_COLOR
        : PIE_COLORS[i % PIE_COLORS.length],
    }));
  }

  return [];
};

const injectSvgLegend = (svg, legendItems) => {
  if (!svg || !legendItems?.length) return NOOP;
  if (svg.querySelector(".exported-legend")) return NOOP;

  const originalWidthAttr = svg.getAttribute("width");
  const originalHeightAttr = svg.getAttribute("height");
  const originalViewBox = svg.getAttribute("viewBox");
  const hadViewBox = svg.hasAttribute("viewBox");

  const fontSize = 13;
  const swatchSize = 12;
  const swatchTextGap = 8;
  const itemGap = 28;
  const padX = 16;
  const padTop = 14;
  const padBottom = 14;
  const rowHeight = Math.max(swatchSize, fontSize) + 6;
  const charWidth = fontSize * 0.55;

  const rect = svg.getBoundingClientRect();
  const widthAttr = Number(svg.getAttribute("width"));
  const heightAttr = Number(svg.getAttribute("height"));
  const width = rect.width || widthAttr || svg.clientWidth || 800;
  const height = rect.height || heightAttr || svg.clientHeight || 600;

  const itemsWithW = legendItems.map((it) => {
    const labelW = (it.label?.length || 0) * charWidth;
    return {
      ...it,
      width: swatchSize + swatchTextGap + labelW,
    };
  });

  const available = width - 2 * padX;
  const rows = [];
  let row = [];
  let rowW = 0;
  itemsWithW.forEach((it) => {
    const addW = (row.length ? itemGap : 0) + it.width;
    if (rowW + addW > available && row.length) {
      rows.push(row);
      row = [];
      rowW = 0;
    }
    row.push(it);
    rowW += addW;
  });
  if (row.length) rows.push(row);

  const legendHeight = padTop + rows.length * rowHeight + padBottom;
  const newHeight = height + legendHeight;

  svg.setAttribute("width", width);
  svg.setAttribute("height", newHeight);
  const vb = svg.getAttribute("viewBox");
  if (vb) {
    const parts = vb.split(/\s+/).map(Number);
    if (parts.length === 4) {
      svg.setAttribute(
        "viewBox",
        `${parts[0]} ${parts[1]} ${parts[2]} ${parts[3] + legendHeight}`,
      );
    }
  } else {
    svg.setAttribute("viewBox", `0 0 ${width} ${newHeight}`);
  }

  const g = document.createElementNS(SVG_NS, "g");
  g.setAttribute("class", "exported-legend");
  g.setAttribute("transform", `translate(0, ${height})`);

  const bg = document.createElementNS(SVG_NS, "rect");
  bg.setAttribute("x", "0");
  bg.setAttribute("y", "0");
  bg.setAttribute("width", String(width));
  bg.setAttribute("height", String(legendHeight));
  bg.setAttribute("fill", "#ffffff");
  g.appendChild(bg);

  rows.forEach((rowItems, rowIdx) => {
    const totalRowW =
      rowItems.reduce((sum, it) => sum + it.width, 0) +
      itemGap * (rowItems.length - 1);
    let x = (width - totalRowW) / 2;
    const y = padTop + rowIdx * rowHeight + rowHeight / 2;

    rowItems.forEach((it) => {
      if (it.dashed) {
        const line = document.createElementNS(SVG_NS, "line");
        line.setAttribute("x1", String(x));
        line.setAttribute("y1", String(y));
        line.setAttribute("x2", String(x + swatchSize));
        line.setAttribute("y2", String(y));
        line.setAttribute("stroke", it.color || "#000");
        line.setAttribute("stroke-width", "2");
        line.setAttribute("stroke-dasharray", "4 3");
        g.appendChild(line);
      } else {
        const sw = document.createElementNS(SVG_NS, "rect");
        sw.setAttribute("x", String(x));
        sw.setAttribute("y", String(y - swatchSize / 2));
        sw.setAttribute("width", String(swatchSize));
        sw.setAttribute("height", String(swatchSize));
        sw.setAttribute("rx", "2");
        sw.setAttribute("fill", it.color || "#000");
        g.appendChild(sw);
      }

      const text = document.createElementNS(SVG_NS, "text");
      text.setAttribute("x", String(x + swatchSize + swatchTextGap));
      text.setAttribute("y", String(y));
      text.setAttribute("dominant-baseline", "middle");
      text.setAttribute("font-family", "Arial, Helvetica, sans-serif");
      text.setAttribute("font-size", String(fontSize));
      text.setAttribute("fill", "#4D5F6E");
      text.textContent = it.label || "";
      g.appendChild(text);

      x += it.width + itemGap;
    });
  });

  svg.appendChild(g);

  return () => {
    if (g.parentNode === svg) svg.removeChild(g);
    if (originalWidthAttr === null) svg.removeAttribute("width");
    else svg.setAttribute("width", originalWidthAttr);
    if (originalHeightAttr === null) svg.removeAttribute("height");
    else svg.setAttribute("height", originalHeightAttr);
    if (hadViewBox) svg.setAttribute("viewBox", originalViewBox);
    else svg.removeAttribute("viewBox");
  };
};

// ── Pagination shapers for offscreen export rendering ──
const buildExportChunks = (graph, graphType) => {
  if (!graph || !isPaginatableChartType(graphType)) return null;
  const data = graph?.data || [];
  if (data.length <= POINTS_PER_PAGE) return null;

  if (graphType === "multiLineGraphs") {
    const formatted = formatMultiLineGraph(graph);
    const dataChunks = chunkSeriesWithOverlap(
      formatted.chartData,
      POINTS_PER_PAGE,
    );
    return dataChunks.map((chunk) => ({ chartData: chunk }));
  }
  if (graphType === "composedBarLineChart") {
    const formatted = formatComposedChart(graph);
    const dataChunks = chunkSeriesWithOverlap(
      formatted.chartData,
      POINTS_PER_PAGE,
    );
    return dataChunks.map((chunk) => ({ chartData: chunk }));
  }
  if (graphType === "barGraph") {
    return chunkArray(data, POINTS_PER_PAGE).map((chunk) => ({ data: chunk }));
  }
  return null;
};

const buildSharedChartProps = (graph, graphType) => {
  if (!graph) return {};
  const xKey = graph?.xKey;

  if (graphType === "multiLineGraphs") {
    const yKeys = graph?.yKey || [];
    const formatted = formatMultiLineGraph(graph);
    const fullDomain = getDomain(formatted.chartData, formatted.lines);
    return {
      lines: formatted.lines,
      legendItems: formatted.legendItems,
      yTicks: formatted.yTicks,
      domain: fullDomain,
      xKey: formatted.xKey,
      yKeys,
      thresholdProps: {
        threshold: graph?.styles?.threshold,
        thresholdColor: graph?.styles?.thresholdColor,
        aboveThresholdColor: graph?.styles?.aboveThresholdColor,
        belowThresholdColor: graph?.styles?.belowThresholdColor,
      },
    };
  }
  if (graphType === "composedBarLineChart") {
    return {
      barKey: graph?.yKey,
      lineKey: graph?.line,
      areaKey: null,
      xKey,
    };
  }
  if (graphType === "barGraph") {
    return {
      xKey,
      yKey: graph?.yKey,
      variant: graph?.variant,
      thresholdProps: {
        threshold: graph?.styles?.threshold,
        thresholdColor: graph?.styles?.thresholdColor,
        aboveThresholdColor: graph?.styles?.aboveThresholdColor,
        belowThresholdColor: graph?.styles?.belowThresholdColor,
      },
    };
  }
  return {};
};

// ── Render a chart chunk into an offscreen slot (used to rasterize per page) ──
const renderExportChartChunk = (type, chunkConfig, shared) => {
  switch (type) {
    case "multiLineGraphs":
      return (
        <MultiLineChart
          chartData={chunkConfig.chartData}
          lines={shared.lines}
          legendItems={shared.legendItems}
          yTicks={shared.yTicks}
          domain={shared.domain}
          xKey={shared.xKey}
          {...(shared.thresholdProps || {})}
        />
      );
    case "composedBarLineChart":
      return (
        <CustomComposedChart
          data={chunkConfig.chartData}
          barKey={shared.barKey}
          lineKey={shared.lineKey}
          areaKey={shared.areaKey}
          xKey={shared.xKey}
          loading={false}
        />
      );
    case "barGraph":
      if (shared.variant === "vertical") {
        return (
          <VerticalBarChart
            data={chunkConfig.data}
            name={shared.xKey}
            value={shared.yKey}
            {...(shared.thresholdProps || {})}
          />
        );
      }
      return (
        <HorizontalBarChart
          data={chunkConfig.data}
          name={shared.xKey}
          value={shared.yKey}
          {...(shared.thresholdProps || {})}
        />
      );
    default:
      return null;
  }
};

// ── Hook — encapsulates all export state, refs, handlers, and offscreen render ──
export const useChartExport = ({
  msg,
  headerConfig,
  rows,
  title,
  lastRefreshed,
  activeView,
  graphType,
  openExpand,
  scopeId,
}) => {
  const [exportAnchor, setExportAnchor] = useState(null);
  const [exportPages, setExportPages] = useState(null);
  const exportResolveRef = useRef(null);
  const exportContainerRef = useRef(null);
  const contentRef = useRef(null);
  const expandedContentRef = useRef(null);

  const fullExportTitle = msg?.graph?.title || title;
  const exportFileBase = sanitizeFilename(fullExportTitle);

  const getExportableData = useCallback(() => {
    if (activeView === "graph") {
      return buildGraphExportData(msg?.graph || msg?.[0]);
    }
    return buildTableExportData(headerConfig, rows);
  }, [activeView, msg, headerConfig, rows]);

  const getActiveContentNode = useCallback(() => {
    const refNode = openExpand
      ? expandedContentRef.current
      : contentRef.current;
    if (refNode) return refNode;
    if (!scopeId) return null;
    const scope = openExpand ? "expanded" : "default";
    return (
      document.querySelector(
        `[data-export-id="${scopeId}"][data-export-scope="${scope}"]`,
      ) || document.querySelector(`[data-export-id="${scopeId}"]`)
    );
  }, [openExpand, scopeId]);

  const handleExportOpen = useCallback((e) => {
    e.stopPropagation();
    setExportAnchor(e.currentTarget);
  }, []);

  const handleExportClose = useCallback(() => setExportAnchor(null), []);

  const handleExportExcel = useCallback(() => {
    const { headers, dataRows } = getExportableData();
    if (!headers.length) {
      handleExportClose();
      return;
    }
    const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${exportFileBase}.xlsx`,
    );
    handleExportClose();
  }, [getExportableData, exportFileBase, handleExportClose]);

  const handleExportCSV = useCallback(() => {
    const { headers, dataRows } = getExportableData();
    if (!headers.length) {
      handleExportClose();
      return;
    }
    const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
    const csv = XLSX.utils.sheet_to_csv(ws);
    saveAs(
      new Blob(["�" + csv], { type: "text/csv;charset=utf-8" }),
      `${exportFileBase}.csv`,
    );
    handleExportClose();
  }, [getExportableData, exportFileBase, handleExportClose]);

  const buildHeatmapHtml = useCallback(() => {
    const graphSrc = msg?.graph || msg?.[0];
    const { columns, data, rowHeaderTitle } = formatHeatMapData(graphSrc);
    if (!columns.length || !data.length) return "";
    return heatmapToHtml(
      rowHeaderTitle,
      columns,
      data,
      fullExportTitle,
      graphSrc?.lastRefreshed || lastRefreshed,
    );
  }, [msg, fullExportTitle, lastRefreshed]);

  const isHeatmapGraphView = activeView === "graph" && graphType === "heatMap";

  const handleExportPNG = useCallback(async () => {
    try {
      if (isHeatmapGraphView) {
        const html = buildHeatmapHtml();
        if (!html) {
          handleExportClose();
          return;
        }
        const blob = await htmlToPngBlob(html);
        saveAs(blob, `${exportFileBase}.png`);
        handleExportClose();
        return;
      }

      const node = getActiveContentNode();
      const svg = node?.querySelector("svg");
      if (svg) {
        const graphSrc = msg?.graph || msg?.[0];
        const legendItems = buildExportLegendItems(graphSrc, graphType);
        const cleanupLegend = injectSvgLegend(svg, legendItems);
        try {
          const blob = await svgToPngBlob(svg);
          saveAs(blob, `${exportFileBase}.png`);
        } finally {
          cleanupLegend();
        }
      } else {
        const { headers, dataRows } = getExportableData();
        if (!headers.length) {
          handleExportClose();
          return;
        }
        const html = tableDataToHtml(headers, dataRows, fullExportTitle);
        const blob = await htmlToPngBlob(html);
        saveAs(blob, `${exportFileBase}.png`);
      }
    } catch (err) {
      console.error("PNG export failed", err);
    }
    handleExportClose();
  }, [
    isHeatmapGraphView,
    buildHeatmapHtml,
    getActiveContentNode,
    getExportableData,
    exportFileBase,
    fullExportTitle,
    handleExportClose,
    msg,
    graphType,
  ]);

  const captureExportPagesAsPngs = useCallback(
    (graph, type) =>
      new Promise((resolve, reject) => {
        const chunkConfigs = buildExportChunks(graph, type);
        if (!chunkConfigs?.length) {
          reject(new Error("No chunks to paginate"));
          return;
        }
        const shared = buildSharedChartProps(graph, type);
        exportResolveRef.current = { resolve, reject };
        setExportPages({ graphType: type, chunks: chunkConfigs, shared });
      }),
    [],
  );

  useEffect(() => {
    if (!exportPages || !exportResolveRef.current) return;
    let cancelled = false;
    (async () => {
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => setTimeout(r, 1700));
      if (cancelled || !exportResolveRef.current) return;
      try {
        const node = exportContainerRef.current;
        if (!node) throw new Error("export container not mounted");
        const slots = Array.from(
          node.querySelectorAll("[data-export-chunk-slot]"),
        );
        const blobs = [];
        const graphSrc = msg?.graph || msg?.[0];
        const legendItems = buildExportLegendItems(
          graphSrc,
          exportPages.graphType,
        );
        for (const slot of slots) {
          const svg = slot.querySelector("svg");
          if (!svg) continue;
          const cleanupLegend = injectSvgLegend(svg, legendItems);
          try {
            blobs.push(await svgToPngBlob(svg));
          } catch (capErr) {
            try {
              blobs.push(await containerToPngBlob(slot));
            } catch (fbErr) {
              throw fbErr;
            }
          } finally {
            cleanupLegend();
          }
        }
        if (!blobs.length) throw new Error("No chunks captured");
        exportResolveRef.current.resolve(blobs);
      } catch (err) {
        exportResolveRef.current?.reject(err);
      } finally {
        exportResolveRef.current = null;
        setExportPages(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exportPages, msg]);

  const handleExportPDF = useCallback(async () => {
    let body = "";
    try {
      if (isHeatmapGraphView) {
        body = buildHeatmapHtml();
        if (!body) {
          handleExportClose();
          return;
        }
        openPrintWindow(body, fullExportTitle);
        handleExportClose();
        return;
      }

      if (activeView === "graph" && isPaginatableChartType(graphType)) {
        const graphSrc = msg?.graph || msg?.[0];
        const totalPoints = graphSrc?.data?.length || 0;
        if (totalPoints > POINTS_PER_PAGE) {
          try {
            const blobs = await captureExportPagesAsPngs(graphSrc, graphType);
            const dataUrls = await Promise.all(blobs.map(blobToDataUrl));
            body = buildMultiPageGraphPdfHtml({
              dataUrls,
              title: fullExportTitle,
              width: EXPORT_CHART_WIDTH,
              height: EXPORT_CHART_HEIGHT,
              totalPoints,
              pointsPerPage: POINTS_PER_PAGE,
            });
            openPrintWindow(body, fullExportTitle);
            handleExportClose();
            return;
          } catch (err) {
            console.warn(
              "Paginated graph export failed; falling back to single image",
              err,
            );
          }
        }
      }

      const node = getActiveContentNode();
      const svg = node?.querySelector("svg");
      if (svg) {
        const { width, height } = getSvgDimensions(svg);
        const graphSrc = msg?.graph || msg?.[0];
        const legendItems = buildExportLegendItems(graphSrc, graphType);
        const cleanupLegend = injectSvgLegend(svg, legendItems);
        let blob;
        try {
          try {
            blob = await svgToPngBlob(svg);
          } catch (capErr) {
            console.warn(
              "svgToPngBlob failed; falling back to containerToPngBlob",
              capErr,
            );
            blob = await containerToPngBlob(node);
          }
        } finally {
          cleanupLegend();
        }
        const dataUrl = await blobToDataUrl(blob);
        body = buildSvgPdfHtml({
          dataUrl,
          width,
          height,
          title: fullExportTitle,
        });
      } else {
        const { headers, dataRows } = getExportableData();
        body = tableDataToHtml(headers, dataRows, fullExportTitle);
      }
      openPrintWindow(body, fullExportTitle);
    } catch (err) {
      console.error("PDF export failed", err);
    }
    handleExportClose();
  }, [
    isHeatmapGraphView,
    buildHeatmapHtml,
    activeView,
    graphType,
    msg,
    captureExportPagesAsPngs,
    getActiveContentNode,
    getExportableData,
    fullExportTitle,
    handleExportClose,
  ]);

  const menuItems = useMemo(() => {
    const common = [
      {
        key: "pdf",
        label: "Export as PDF",
        icon: pdfIcon,
        onClick: handleExportPDF,
      },
    ];
    if (activeView === "table") {
      return [
        ...common,
        {
          key: "xlsx",
          label: "Export as Excel",
          icon: excelIcon,
          onClick: handleExportExcel,
        },
        {
          key: "csv",
          label: "Export as CSV",
          icon: csvIcon,
          onClick: handleExportCSV,
        },
      ];
    }
    return [
      ...common,
      ...(graphType !== "heatMap"
        ? [
            {
              key: "png",
              label: "Export as PNG",
              icon: pngIcon,
              onClick: handleExportPNG,
            },
          ]
        : []),
    ];
  }, [
    activeView,
    handleExportPDF,
    handleExportExcel,
    handleExportCSV,
    handleExportPNG,
  ]);

  const exportContainerNode = exportPages ? (
    <Box
      ref={exportContainerRef}
      aria-hidden="true"
      sx={{
        position: "fixed",
        left: -99999,
        top: 0,
        width: EXPORT_CHART_WIDTH,
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      {exportPages.chunks.map((chunkConfig, i) => (
        <Box
          key={i}
          data-export-chunk-slot={i}
          sx={{
            width: EXPORT_CHART_WIDTH,
            minHeight: EXPORT_CHART_HEIGHT,
            display: "flex",
            flexDirection: "column",
            background: "#ffffff",
            overflow: "visible",
            marginBottom: "16px",
          }}
        >
          {renderExportChartChunk(
            exportPages.graphType,
            chunkConfig,
            exportPages.shared,
          )}
        </Box>
      ))}
    </Box>
  ) : null;

  return {
    exportAnchor,
    handleExportOpen,
    handleExportClose,
    menuItems,
    contentRef,
    expandedContentRef,
    exportContainerNode,
  };
};
